import { useCallback, useState } from 'react';

import { useLocalStorage } from '../helpers/localStorage';

type SectionState = Record<string, boolean>;

/**
 * Hook for managing collapsible section state via localStorage.
 *
 * Sections are identified by a unique ID and their open/closed state is
 * stored in localStorage with different keys for different pages.
 *
 * This allows for:
 * - Persistent state across page refreshes
 * - Per-page section state management
 *
 * @param storageKey - Unique key for localStorage (e.g., 'admin-sections', 'internal-tools-sections')
 * @returns Object with methods to check and toggle section state
 */
export const useCollapsibleSections = (storageKey: string) => {
  const localStorage = useLocalStorage<SectionState>(storageKey);

  // Use state to trigger re-renders when section state changes
  const [, forceUpdate] = useState({});

  /**
   * Check if a section is currently open based on localStorage
   *
   * @param sectionId - Unique identifier for the section
   * @returns true if the section is open, false if collapsed
   */
  const isSectionOpen = useCallback(
    (sectionId: string): boolean => {
      const state = localStorage.get();
      return state?.[sectionId] ?? false;
    },
    [localStorage],
  );

  /**
   * Toggle a section's state and update localStorage
   *
   * @param sectionId - Unique identifier for the section
   * @param isCurrentlyOpen - Current open state of the section
   */
  const toggleSection = useCallback(
    (sectionId: string, isCurrentlyOpen: boolean) => {
      const currentState = localStorage.get() ?? {};
      const newState = { ...currentState };

      if (isCurrentlyOpen) {
        // Currently open, so close it (remove from state)
        delete newState[sectionId];
      } else {
        // Currently closed, so open it (add to state)
        newState[sectionId] = true;
      }

      localStorage.set(newState);
      // Force a re-render to update the UI
      forceUpdate({});
    },
    [localStorage],
  );

  return { isSectionOpen, toggleSection };
};
